import React, { useEffect, useRef, useState } from 'react'
import { connect } from 'react-redux'
import { AppUseSelector, AppUseDispatch } from '../../redux/hooks'
import { setAudioRecording } from '../../redux/actions/recorderAction'
import { getSignedUrl } from 'utils/S3Upload'
import {
  GetPendingCorrections,
  GetStudentCorrection,
  getStory,
  getQuestion,
  setCorrections,
} from '../../redux/actions/studentCorrectionsAction'
import Loader from 'containers/loader/loader'
import WordDetail from 'containers/word-detail'
import WordExamplesModal from 'containers/modal/word-examples-modal'
import { CHECK_WORD_IS_CLICKED } from '../../redux/types'
import { converterWord, getWordLine, getWordTypeFromStory, wordSimplified, getSentenceWords } from 'utils/helperFunction'
import { getWordDetail, wordDoesNotKnow } from '../../redux/actions/storyAction'
import { AddWordToHackChinese, handleAddWordToSkritter } from '../../redux/actions/userAction'
import { PendingCorrection, resolveCorrectionsView } from 'utils/correctionsState'
import CorrectionsEmptyState from './CorrectionsEmptyState'
import PendingCorrectionCard from './PendingCorrectionCard'
import CorrectionCard from './CorrectionCard'
import CorrectionCardBoundary from './CorrectionCardBoundary'
import { useNavigate } from 'react-router-dom'
import { STORIES } from 'routes/routeConstant'
import { membershipNameType, PlanChangeLink } from 'utils/constant'
import { openSupportChat } from 'utils/crisp'
import { useTranslation } from 'react-i18next'
import './corrections.css'

/** Enough to show the work is accounted for without burying the finished list. */
const MAX_PINNED_PENDING = 3

interface ICorrectionProps {
  credentials?: any
}
const Corrections: React.FC<ICorrectionProps> = ({
  credentials,
  correctionsMaxCount,
  question,
  questionContent,
  user_auth_token,
  currentStory,
  skritter_token,
  membership,
}: any) => {
  const { t } = useTranslation('corrections')
  const { memberId } = credentials
  const storyId = currentStory?.id
  const answersData = AppUseSelector(({ corrections }) => corrections.Data)
  const { chineseWordFontSize } = AppUseSelector(({ dashboard }) => dashboard)
  const characterPreference = credentials?.customFields?.character
  const [audioRecordingUrl, setAudioRecordingUrl] = useState('')
  const [correctionRecordingUrl, setCorrectionRecordingUrl] = useState('')
  const [isStoryAvailable, setIsStoryAvailable] = useState(false)
  const [loadingMoreStatus, setLoadingMoreStatus] = useState(false)
  const [loadingContentStatus, setLoadingContentStatus] = useState(false)
  // The story behind a correction can fail to load (a plan that no longer
  // opens stories, a story pulled from the archive). The markup itself does not
  // depend on it, so the card says so and carries on rather than spinning
  // forever, which is what the old `.then`-only chain did.
  const [contentError, setContentError] = useState(false)
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(5)
  const [activeIndex, setActiveIndex] = useState('-1')
  const [openStoryId, setOpenStoryId] = useState('')
  const dispatch = AppUseDispatch()
  const navigate = useNavigate()
  const { wordDetails, isWordClick } = AppUseSelector(({ story }) => story)
  const extraDivRef = useRef<HTMLDivElement>(null)
  const contentWordRef = useRef<HTMLDivElement>(null)
  const [wordDetailVisible, setWordDetailVisible] = useState(false)
  const [openExampleModal, setOpenExampleModal] = useState(false)
  const [isImportanceOpen, setImportanceOpen] = useState(false)
  const [isMasteryOpen, setMasteryOpen] = useState(false)
  // Submissions the teacher has not closed yet. `null` means the request has not
  // resolved (or failed), distinct from an empty array, because "we could not
  // check" and "you have nothing waiting" must not say the same thing.
  const [pending, setPending] = useState<PendingCorrection[] | null>(null)
  const [pendingLoaded, setPendingLoaded] = useState(false)

  const openCorrection = answersData?.find((item: any) => item?.storyId === openStoryId)

  const fetchData = async () => {
    const currentCorrections = !answersData ? [...answersData] : []
    const currentPage = page ? page : 1
    const currentPer_Page = perPage + 5
    setPerPage(currentPer_Page)
    const newCorrections: any = await GetStudentCorrection(currentPer_Page, currentPage)
    const allCorrections = currentCorrections.concat([...newCorrections.Data])
    setCorrections(allCorrections)

    if (allCorrections.length < newCorrections.count) setPage(page + 1)
  }

  const handleClickOutside = (event) => {
    const isInsideSpecificButtons = event.target.closest('[data-specific-buttons]')
    if (
      !isInsideSpecificButtons &&
      !contentWordRef.current?.contains(event.target) &&
      !extraDivRef.current?.contains(event.target)
    ) {
      if (wordDetailVisible && wordDetails?.word && !isWordClick) {
        const mastery = getWordTypeFromStory(wordDetails?.word, currentStory, characterPreference)
        wordDoesNotKnow({
          word: wordSimplified(wordDetails?.word),
          storyId,
          mastery: mastery === 'word' ? undefined : mastery,
          page: 'correction',
        })
        dispatch({ type: CHECK_WORD_IS_CLICKED, payload: false })
      }
      setWordDetailVisible(false)
      setImportanceOpen(false)
      setMasteryOpen(false)
      setActiveIndex('-1')
    } else if (isInsideSpecificButtons) {
      if (wordDetailVisible && wordDetails?.word && !isWordClick) {
        const mastery = getWordTypeFromStory(wordDetails?.word, currentStory, characterPreference)
        wordDoesNotKnow({
          word: wordSimplified(wordDetails?.word),
          storyId,
          mastery: mastery === 'word' ? undefined : mastery,
          page: 'correction',
        })
        dispatch({ type: CHECK_WORD_IS_CLICKED, payload: false })
      }
    }
  }

  useEffect(() => {
    if (openExampleModal) {
      return
    }

    document.addEventListener('mousedown', handleClickOutside)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [extraDivRef, contentWordRef, openExampleModal, wordDetailVisible, isWordClick, wordDetails])

  useEffect(() => {
    Promise.resolve(GetStudentCorrection(perPage.toString(), page.toString())).then(() => setIsStoryAvailable(true))
  }, [])

  // Fetched alongside the closed corrections, never blocking them: a failure here
  // only costs the page the difference between "nothing sent yet" and "your work
  // is with the teacher", and the list itself must still render.
  useEffect(() => {
    let cancelled = false
    GetPendingCorrections()
      .then((rows) => {
        if (!cancelled) setPending(rows)
      })
      .catch(() => {
        if (!cancelled) setPending(null)
      })
      .finally(() => {
        if (!cancelled) setPendingLoaded(true)
      })
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    setAudioRecording({})
    setAudioRecordingUrl('')
    setCorrectionRecordingUrl('')
  }, [answersData])

  // Everything an open correction needs beyond what the list already carries:
  // signed audio links, and the story + question it was written against.
  useEffect(() => {
    if (!openStoryId || !openCorrection) return

    let cancelled = false
    setAudioRecordingUrl('')
    setCorrectionRecordingUrl('')
    setContentError(false)
    setLoadingContentStatus(true)

    if (openCorrection.recordingUrl) {
      getSignedUrl({ key: openCorrection.recordingUrl })
        .then((res) => !cancelled && setAudioRecordingUrl(res))
        .catch(() => !cancelled && setAudioRecordingUrl(''))
    }
    if (openCorrection.correctedRecordingUrl) {
      getSignedUrl({ key: openCorrection.correctedRecordingUrl })
        .then((res) => !cancelled && setCorrectionRecordingUrl(res))
        .catch(() => !cancelled && setCorrectionRecordingUrl(''))
    }

    Promise.all([
      getQuestion(openCorrection.storyId, openCorrection.level),
      getStory(openCorrection.storyId, openCorrection.level),
    ])
      .catch(() => {
        if (!cancelled) setContentError(true)
      })
      .finally(() => {
        if (!cancelled) setLoadingContentStatus(false)
      })

    return () => {
      cancelled = true
    }
  }, [openStoryId, openCorrection?.storyId])

  const handle = {
    onClickWord: (event, word, sentence, key) => {
      // On a phone the word panel slides up over the bottom of the screen, so
      // bring the tapped word above it before the panel appears.
      if (window.innerWidth < 768) {
        document.getElementById(key)?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }

      handle.translateWord(event, sentence, word)

      setActiveIndex(activeIndex === key ? `-1` : key)
      event.preventDefault()
      event.stopPropagation()
    },
    translateWord: (event: any, sentence: any, wordInfo: any) => {
      if (
        (event.type === 'mouseover' || event.type === 'click') &&
        event.target.innerText &&
        event.target.innerText.length > 0
      ) {
        const word = event.target.innerText
        // Spread only what is actually a list: the segmentation fields can come
        // back as a bare string, and spreading one splats it into single
        // characters that then match the tapped word by accident.
        const asList = (value: any) => (Array.isArray(value) ? value : [])
        // The open correction, not the first one in the list: looking a word up
        // in someone else's answer returned the wrong pinyin whenever an older
        // correction was the one on screen.
        const allData = [
          ...asList(currentStory?.arContent),
          ...asList(questionContent),
          ...asList(openCorrection?.answerContect),
          ...asList(openCorrection?.correctedAnswerContect),
        ]

        if (/^[&.-”“，、；？@!#$%^&*()]+$/.test(word) || word.trim() === '。') {
          return
        }
        const wordTranslate = allData?.filter((iword) => converterWord(iword?.word, characterPreference) === word)
        if (wordTranslate && wordTranslate.length > 0) {
          handle.getWordDetail(word, sentence, wordTranslate[0])
        } else {
          handle.getWordDetail(word, sentence, wordInfo)
        }
      }
    },
    getWordDetail: async (word: string, sentence: any, data: any) => {
      const sentenceString = getWordLine(sentence)
      const basicDetail = {
        word,
        pinyin: data?.pinyin,
        english: data?.english,
        sentenceString,
        sentenceWords: getSentenceWords(sentence),
      }

      if (word && memberId) {
        setWordDetailVisible(true)
        const mastery = getWordTypeFromStory(word, currentStory, characterPreference)
        await getWordDetail({
          detail: basicDetail,
          payload: { word: wordSimplified(word), memberId },
          wordType: mastery,
        }).then((res) => {
          handle.onWordClickIntegration({ word: res?.word, pinyin: res?.pinyin, english: res?.english })
        })
      }
    },
    onWordClickIntegration: (obj: any) => {
      if (user_auth_token) {
        AddWordToHackChinese({
          word: obj?.word,
          pinyin: obj?.pinyin,
          user_auth_token: user_auth_token,
        })
      }
      if (skritter_token) {
        handleAddWordToSkritter({
          word: obj?.word,
          translate: { pinyin: obj?.pinyin, english: obj?.english },
          title: currentStory?.storyTitleContent,
          skritter_token: skritter_token,
        })
      }
    },
  }

  // What the page has to say when it has no finished correction to show. Kept in
  // one place so the empty state and the pinned pending cards agree.
  const pendingList = pending ?? []
  const view = resolveCorrectionsView({
    closedCount: answersData?.length ?? 0,
    // Fails open while the membership is still loading: flashing "Corrections come
    // with Pro" at a paying Pro learner is the worse mistake. Teacher accounts read
    // this page from the learner side, so they count as entitled too.
    isEntitled:
      !membership?.name || membership.name === membershipNameType.Pro || membership.name === membershipNameType.Teacher,
    pending: pendingList,
    pendingKnown: pending !== null,
  })

  return (
    <>
      <div className='correction-div-main'>
        <div className='section-correction-body  w-container'>
          <div className='section-correction-header'>
            <div className='w-row'>
              <div className='w-col w-col-12 mb-20'>
                <span className='correction-title'> {t('page.title')} </span>
              </div>
            </div>
          </div>

          {!isStoryAvailable ? (
            <Loader />
          ) : (
            <div className='correction-items'>
              {/* Only alongside finished corrections. When the list is empty the
                  empty state below already says the work is with the teacher.
                  Capped: a teacher away for a week would otherwise bury the list
                  the learner came here to read. */}
              {view === 'has-corrections' &&
                pendingList
                  .slice(0, MAX_PINNED_PENDING)
                  .map((item, index) => (
                    <PendingCorrectionCard key={`pending-${item.storyId ?? index}`} correction={item} />
                  ))}

              {answersData?.length > 0 ? (
                answersData.map((item: any) => (
                  <CorrectionCardBoundary key={item.storyId}>
                    <CorrectionCard
                      correction={item}
                      isOpen={openStoryId === item.storyId}
                      onToggle={() => setOpenStoryId(openStoryId === item.storyId ? '' : item.storyId)}
                      isLoading={openStoryId === item.storyId && loadingContentStatus}
                      loadError={openStoryId === item.storyId && contentError}
                      story={currentStory}
                      question={question}
                      questionContent={questionContent}
                      chineseWordFontSize={chineseWordFontSize}
                      activeIndex={activeIndex}
                      onClickWord={handle.onClickWord}
                      recordingUrl={audioRecordingUrl}
                      correctedRecordingUrl={correctionRecordingUrl}
                    />
                  </CorrectionCardBoundary>
                ))
              ) : !pendingLoaded ? (
                // Still checking the queue. Showing "no corrections yet" here and
                // correcting it a moment later is the confusion we are fixing.
                <Loader />
              ) : view === 'has-corrections' ? null : (
                <CorrectionsEmptyState
                  kind={view}
                  pending={pendingList}
                  onGoToStories={() => navigate(STORIES)}
                  onContactSupport={() => openSupportChat({ email: credentials?.email })}
                  onUpgrade={() => navigate(PlanChangeLink)}
                />
              )}

              {loadingMoreStatus && <Loader />}
              {!loadingMoreStatus && correctionsMaxCount >= perPage && (
                <div
                  className='see-more-btn text-center'
                  onClick={() => {
                    setLoadingMoreStatus(true)
                    setOpenStoryId('')
                    Promise.resolve(fetchData()).then(() => setLoadingMoreStatus(false))
                  }}
                >
                  <button>
                    {t('page.showMore')}{' '}
                    <span>
                      <i className='fa fa-chevron-down'></i>
                    </span>
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      <WordDetail
        ref={contentWordRef}
        setOpenExampleModal={setOpenExampleModal}
        isImportanceOpen={isImportanceOpen}
        setImportanceOpen={setImportanceOpen}
        isMasteryOpen={isMasteryOpen}
        setMasteryOpen={setMasteryOpen}
        setWordDetailVisible={setWordDetailVisible}
        setActiveIndex={setActiveIndex}
        visible={wordDetailVisible}
        openExampleModal={openExampleModal}
        showMasterySection={true}
      />
      <WordExamplesModal
        ref={extraDivRef}
        openExampleModal={openExampleModal}
        setOpenExampleModal={setOpenExampleModal}
        setWordDetailVisible={setWordDetailVisible}
      />
    </>
  )
}
const mapStateToProps = (state: any) => ({
  user_auth_token: state.user.user_auth_token,
  skritter_token: state.user.skritter_token,
  credentials: state.user.credentials,
  membership: state.user.membership,
  currentStory: state.corrections.currentStory,
  question: state.corrections.question,
  questionContent: state.corrections.questionContent,
  correctionsMaxCount: state.corrections.correctionsMaxCount,
})
export default connect(mapStateToProps)(Corrections)
